import { useState, useEffect, useCallback } from 'react';
import {
  Search, MapPin, Bell, BellOff, Clock, Plus, RefreshCw, TrendingUp, Filter
} from 'lucide-react';
import { API } from '../../utils/apiConfig';

function formatDate(dateStr) {
  if (!dateStr) return '-';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return dateStr; }
}

function formatDateTime(dtStr) {
  if (!dtStr) return '-';
  try {
    const d = new Date(dtStr);
    return d.toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
    });
  } catch { return dtStr; }
}

export const AdminSearchInsightsTab = () => {
  const [searches, setSearches] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [notifyOnly, setNotifyOnly] = useState(false);

  const getHeaders = () => {
    const token = localStorage.getItem('token');
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = notifyOnly ? '?notify_only=true&limit=100' : '?limit=100';
      const res = await fetch(`${API}/orchestrator/admin/search-insights${params}`, {
        headers: getHeaders(),
      });
      const data = await res.json();
      setSearches(data.searches || []);
      setTotal(data.total || 0);
    } catch (e) {
      console.error('Search Insights load failed:', e);
    } finally {
      setLoading(false);
    }
  }, [notifyOnly]);

  useEffect(() => { loadData(); }, [loadData]);

  const hotLeads = searches.filter(s => s.notification_active).length;

  return (
    <div data-testid="admin-search-insights" style={{ padding: '0 0 32px' }}>
      {/* Header Stats */}
      <div style={{
        display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap',
      }}>
        <div data-testid="search-insights-total" style={{
          flex: '1 1 200px', padding: '20px 24px',
          background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
          borderRadius: 12, border: '1px solid rgba(255,255,255,0.06)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <Search size={18} style={{ color: '#94a3b8' }} />
            <span style={{ color: '#94a3b8', fontSize: 13, fontWeight: 500 }}>Fehlgeschlagene Suchen</span>
          </div>
          <div style={{ fontSize: 32, fontWeight: 700, color: '#f1f5f9' }}>{total}</div>
        </div>

        <div data-testid="search-insights-hot-leads" style={{
          flex: '1 1 200px', padding: '20px 24px',
          background: hotLeads > 0
            ? 'linear-gradient(135deg, #0c4a6e 0%, #082f49 100%)'
            : 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
          borderRadius: 12,
          border: hotLeads > 0 ? '1px solid #0ea5e9' : '1px solid rgba(255,255,255,0.06)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <Bell size={18} style={{ color: hotLeads > 0 ? '#38bdf8' : '#94a3b8' }} />
            <span style={{ color: hotLeads > 0 ? '#7dd3fc' : '#94a3b8', fontSize: 13, fontWeight: 500 }}>
              Hot Leads (Benachrichtigung aktiv)
            </span>
          </div>
          <div style={{ fontSize: 32, fontWeight: 700, color: hotLeads > 0 ? '#38bdf8' : '#f1f5f9' }}>
            {hotLeads}
          </div>
        </div>

        <div style={{
          flex: '1 1 200px', padding: '20px 24px',
          background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
          borderRadius: 12, border: '1px solid rgba(255,255,255,0.06)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <TrendingUp size={18} style={{ color: '#94a3b8' }} />
            <span style={{ color: '#94a3b8', fontSize: 13, fontWeight: 500 }}>Top-Route</span>
          </div>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#f1f5f9' }}>
            {searches.length > 0
              ? `${searches[0].from_city || '?'} → ${searches[0].to_city || '?'}`
              : 'Keine Daten'}
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 16, gap: 12, flexWrap: 'wrap',
      }}>
        <button
          data-testid="search-insights-filter-hot"
          onClick={() => setNotifyOnly(!notifyOnly)}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '8px 16px', borderRadius: 8, cursor: 'pointer',
            fontSize: 13, fontWeight: 500, transition: 'all 0.2s',
            background: notifyOnly ? 'rgba(14,165,233,0.15)' : 'rgba(255,255,255,0.05)',
            border: notifyOnly ? '1px solid #0ea5e9' : '1px solid rgba(255,255,255,0.1)',
            color: notifyOnly ? '#38bdf8' : '#94a3b8',
          }}
        >
          <Filter size={14} />
          {notifyOnly ? 'Nur Hot Leads' : 'Alle Suchen'}
        </button>

        <button
          data-testid="search-insights-refresh"
          onClick={loadData}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '8px 14px', borderRadius: 8, cursor: 'pointer',
            background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
            color: '#94a3b8', fontSize: 13,
          }}
        >
          <RefreshCw size={14} /> Aktualisieren
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>Laden...</div>
      ) : searches.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: 60, color: '#475569',
          background: 'rgba(255,255,255,0.02)', borderRadius: 12,
          border: '1px solid rgba(255,255,255,0.05)',
        }}>
          <Search size={32} style={{ marginBottom: 12, opacity: 0.3 }} />
          <p style={{ fontSize: 15 }}>Keine fehlgeschlagenen Suchen gefunden</p>
        </div>
      ) : (
        <div style={{
          borderRadius: 12, overflow: 'hidden',
          border: '1px solid rgba(255,255,255,0.06)',
        }}>
          <table data-testid="search-insights-table" style={{
            width: '100%', borderCollapse: 'collapse', fontSize: 13,
          }}>
            <thead>
              <tr style={{ background: 'rgba(255,255,255,0.03)' }}>
                <th style={thStyle}>User</th>
                <th style={thStyle}>Route</th>
                <th style={thStyle}>Datum/Zeit</th>
                <th style={thStyle}>Distanz</th>
                <th style={thStyle}>Max-Preis</th>
                <th style={thStyle}>Notify</th>
                <th style={thStyle}>Erstellt</th>
                <th style={thStyle}>Aktion</th>
              </tr>
            </thead>
            <tbody>
              {searches.map((s) => (
                <tr
                  key={s.id}
                  data-testid={`search-row-${s.id}`}
                  style={{
                    background: s.notification_active
                      ? 'rgba(14, 165, 233, 0.08)'
                      : 'transparent',
                    borderBottom: '1px solid rgba(255,255,255,0.04)',
                    transition: 'background 0.2s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = s.notification_active ? 'rgba(14,165,233,0.14)' : 'rgba(255,255,255,0.03)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = s.notification_active ? 'rgba(14,165,233,0.08)' : 'transparent'; }}
                >
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 500, color: '#e2e8f0' }}>{s.user_name || 'Unbekannt'}</div>
                    <div style={{ fontSize: 11, color: '#64748b' }}>{s.user_email || ''}</div>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <MapPin size={13} style={{ color: '#22c55e', flexShrink: 0 }} />
                      <span style={{ color: '#e2e8f0' }}>
                        {s.from_street ? `${s.from_street}, ` : ''}{s.from_city || '?'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                      <MapPin size={13} style={{ color: '#ef4444', flexShrink: 0 }} />
                      <span style={{ color: '#e2e8f0' }}>
                        {s.to_street ? `${s.to_street}, ` : ''}{s.to_city || '?'}
                      </span>
                    </div>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ color: '#e2e8f0' }}>{formatDate(s.requested_date)}</div>
                    <div style={{ color: '#94a3b8', fontSize: 12 }}>
                      <Clock size={11} style={{ verticalAlign: 'middle', marginRight: 3 }} />
                      {s.requested_time || '-'}
                    </div>
                  </td>
                  <td style={{ ...tdStyle, color: '#e2e8f0' }}>
                    {s.distance_km ? `${parseFloat(s.distance_km).toFixed(0)} km` : '-'}
                  </td>
                  <td style={{ ...tdStyle, color: '#e2e8f0' }}>
                    {s.max_price ? `${parseFloat(s.max_price).toFixed(0)}€` : '-'}
                  </td>
                  <td style={tdStyle}>
                    {s.notification_active ? (
                      <span data-testid={`notify-active-${s.id}`} style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                        background: 'rgba(14,165,233,0.2)', color: '#38bdf8',
                        border: '1px solid rgba(14,165,233,0.3)',
                      }}>
                        <Bell size={11} /> Aktiv
                      </span>
                    ) : (
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        padding: '3px 10px', borderRadius: 20, fontSize: 11,
                        color: '#475569',
                      }}>
                        <BellOff size={11} /> Nein
                      </span>
                    )}
                  </td>
                  <td style={{ ...tdStyle, color: '#94a3b8', fontSize: 12 }}>
                    {formatDateTime(s.created_at)}
                  </td>
                  <td style={tdStyle}>
                    <button
                      data-testid={`create-ride-${s.id}`}
                      onClick={() => {
                        const params = new URLSearchParams({
                          from: s.from_city || '',
                          to: s.to_city || '',
                          date: s.requested_date || '',
                          time: s.requested_time || '',
                        });
                        window.open(`/ride/create?${params.toString()}`, '_blank');
                      }}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        padding: '6px 12px', borderRadius: 6, cursor: 'pointer',
                        background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)',
                        color: '#4ade80', fontSize: 12, fontWeight: 500,
                        transition: 'all 0.2s', whiteSpace: 'nowrap',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(34,197,94,0.2)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'rgba(34,197,94,0.1)'; }}
                    >
                      <Plus size={13} /> Fahrt erstellen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const thStyle = {
  padding: '12px 16px', textAlign: 'left', fontWeight: 600,
  color: '#94a3b8', fontSize: 11, textTransform: 'uppercase',
  letterSpacing: '0.05em', borderBottom: '1px solid rgba(255,255,255,0.06)',
};

const tdStyle = {
  padding: '12px 16px', verticalAlign: 'top',
};
